import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { AgreementsFormOptions } from '../form-interfaces/agreements-form-options.interface';
import { BehaviorSubject, finalize, Subject, Subscription, takeUntil } from 'rxjs';
import { Agency } from '@lmc-app/shared/interface/agency.interface';
import { ModuleFieldPicklist } from '@lmc-app/shared/interface/module-field-picklist';
import { getCalculatorReferralFields } from '../form-interfaces/agreements-referral-interface';
import { ZohoService } from '@lmc-app/core/services/zoho.service';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { callLoader, flagMissingField, getFormlyChangedValues, isMissingField, openSnackBar } from '../../helper-functions/docusign-utils';
import dayjs from 'dayjs';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AgreementLoanCalculatorService } from '../services/agreements-calculator.service';
import { LoancalculatorComponent } from '@lmc-app/summary/loancalculator/loancalculator.component';
import { OldLoanCalculatorComponent } from '@lmc-app/summary/old-loan-calculator/old-loan-calculator.component';

@Component({
  selector: 'app-agreements-calculator-modal',
  templateUrl: './agreements-calculator-modal.component.html',
  styleUrls: ['./agreements-calculator-modal.component.scss']
})

export class AgreementsCalculatorModalComponent implements OnInit {            
	public data = inject(MAT_DIALOG_DATA, { optional: true }); 	// Agreement Data
	private _agreementCalculatorService = inject(AgreementLoanCalculatorService);	// Emit completed i.e true to agreement to generate updates
  	private sseSubscription!: Subscription;
	private destroy$ = new Subject<void>();
	
	isSahaCalculator:boolean = true;
	applicationIdFromModal: string;
	currentLoggedEmployeeId: number;
	showRefresh:boolean = false;
	constructor(
		private _docusignService: DocusignService,  
		private readonly zohoService: ZohoService, 
		private readonly apiService: LegacyApiService,
		private _bottomSheet: MatBottomSheet,
		private _snackBar: MatSnackBar,  
	){ this.isSahaCalculator = this.data?.HasSaha; }
    
	ngOnInit(): void {
		this.initializeFormConfig();  
		this.applicationIdFromModal = this.data?.ApplicationId.value;
		this.currentLoggedEmployeeId = this.data?.userInfo?.employeeID;

		this.verifyWaiveOff();	
		this._agreementCalculatorService.dialogResult$
			.pipe(takeUntil(this.destroy$)) // avoid memory leaks
			.subscribe(async (result) => {
			if(result?.source === 'waiveOff-request'){
				this.showRefresh = true;
			} 
		})
	}

	formlyForm = new FormGroup({});   // Initialize the FormGroup correctly
	formlyFields: FormlyFieldConfig[] = [];   // separate variable for the field configuration array
	dataModel = {};
	private formOptions: Partial<AgreementsFormOptions>;
	private parentSub = new Subscription();

	private agencies$ = new BehaviorSubject<Agency[]>([]);
	private creditors$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private provinces$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private creditorPortfolios$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private additionalDebtCreditorPortfolios$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private loanTypes$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);

	private loadDropdownOptions() {
		this.getAgencies();
		this.getCreditors();
		this.getProvinces();
		this.getCreditorPortfolio();
		this.getAdditionalDebtCreditorPortfolio();
	}

  private getProvinces() {
		this.apiService.getModuleField('Deals', 'Province1').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.provinces$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

  private getAgencies() {
		this.zohoService.getAgencies().subscribe({
			next: (res) => {
				this.agencies$.next(res);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

  private getAgencyNameById(agencyId: string): string {
		if (!agencyId) return 'No Agency Selected';
		const agencies = this.agencies$.getValue();
		const agency = agencies.find((a) => a.id === agencyId);
		return agency?.Account_Name || 'Selected Agency';
	}

  private getCreditors() {
		this.apiService.getModuleField('Deals', 'Creditt').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.creditors$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	private async initializeFormConfig() {
		this.loadDropdownOptions();
		await this.mapFormWithCard();
	}  
  
	private getCreditorPortfolio() {
		this.parentSub.add(this.apiService.getModuleField('Deals', 'Creditor_Portfolio').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.creditorPortfolios$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	private getAdditionalDebtCreditorPortfolio() {
		this.parentSub.add(this.apiService.getModuleField('Additional_Debt', 'Creditor_Portfolio').subscribe({
			next: (res) => {
				const filtered = [{ display_value: '', reference_value: '', actual_value: '' }, ...res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				)];
				this.additionalDebtCreditorPortfolios$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		}));
	}

	async mapFormWithCard(){      
		this.formOptions = {
		creditors$: this.creditors$,
		creditorPortfolios$: this.creditorPortfolios$,
		additionalDebtCreditorPortfolios$: this.additionalDebtCreditorPortfolios$,
				loanTypes$: this.loanTypes$,
		agencies$: this.agencies$,
		getAgencyName: (agencyId: string) => this.getAgencyNameById(agencyId),
		onUpdate: (model: any) => {
			// console.log(model,"++++++++++++++++++++")
        	this.saveChanges()
		},
		onSkip: () => this.skipToCalculator(),
		};
		this.formlyFields = [
		...getCalculatorReferralFields('col-sm-6 col-lg-4',this.formOptions)      
		]
		setTimeout(() => this.flagMissingFields());   // Wait for formly to build the controls
	}

	// Show the controls for values flagged as missing in red
	private flagMissingFields(): void {
		const missingKeys = new Set(Object.entries(this.data ?? {})
			.filter(([, f]) => isMissingField(f))
			.map(([key]) => `${key}.value`));
		const visit = (fields: FormlyFieldConfig[] = []) => fields.forEach((f) => {
			if (missingKeys.has(f.key as string) && f.formControl) {
				flagMissingField(f);
			}
			visit(f.fieldGroup);
		});
		visit(this.formlyFields);
	}

	saveChanges() {
		const ref = callLoader(this._bottomSheet, 'Saving changes...');
		const updatedData = getFormlyChangedValues(this.formlyForm, this.data);
		delete updatedData.originalData;
		const liveAdditionalDebt: any[] = this.data?.originalData?.Additional_Debt ?? [];
		const additionalDebtForSave = liveAdditionalDebt.map((debt: any) => {
			if (!debt?._delete) {
				return debt;
			}
			return { id: debt.id, _delete: null };
		});
		const zohoUpdateData = Object.assign(
			{},
			...Object.values(updatedData),
			{ Additional_Debt: additionalDebtForSave },
			{ Override_Type: `${this.data.userInfo?.full_name} (${dayjs().format('YYYY-MM-DD HH:mm:ss')})`}		)
		
		this._docusignService.saveDocusignZohoChanges(this.data.ApplicationId?.value, zohoUpdateData, null).pipe(
			finalize(() => {
			ref.dismiss();
			})
		).subscribe(res=>{
			// // this._agreementDataService.emitResult('referral-update', this.data.ApplicationId?.value);
			if(res){
				console.log(res,"saved changes+++++++++")
			}
			this.findFieldByKey(this.formlyFields, 'originalData.Additional_Debt')?.props?.purgeDeleted?.();
        	openSnackBar(this._snackBar, 'success', `Saved ${res?.data[0]?.message}`)
			this.formlyForm.markAsPristine();	// Saved, so Skip is available again when coming back to Debts
			this.goToCalculator();
		})
	}

	private findFieldByKey(fields: FormlyFieldConfig[], key: string): FormlyFieldConfig | undefined {
		for (const field of fields || []) {
			if (field.key === key) {
				return field;
			}
			const found = this.findFieldByKey(field.fieldGroup, key);
			if (found) {
				return found;
			}
		}
		return undefined;
	}

	getConfigForSahaFile(indicator: any, fileKey: string): boolean {
		return this._agreementCalculatorService.getConfig(indicator,fileKey);
	}

	isWaivedOff(applicationId: string, employeeID: number): Promise<{ activeCount: number; exceptionCount: any; isApproved: any }>{
		return new Promise((resolve, reject) => {
			this.apiService
				.verifyWaiveOff(applicationId, employeeID)
				.subscribe({
					next: (res: { activeCount: number; exceptionCount: any; isApproved: any }) => resolve(res),
					error: (err) => reject(err)
					});
		});
	}

	async verifyWaiveOff() {
		const waiveOffResult = await this.isWaivedOff(this.applicationIdFromModal, this.currentLoggedEmployeeId);
		this.isSahaCalculator = (!waiveOffResult?.isApproved || (waiveOffResult?.isApproved?.mode === 'Partial' && waiveOffResult?.isApproved?.approved)) 
		? this.isSahaCalculator = true
		: (waiveOffResult?.isApproved?.mode === 'Full' && waiveOffResult?.isApproved?.approved)
		? this.isSahaCalculator = false
		: true;
		this.showRefresh = (waiveOffResult?.isApproved?.mode === 'Full' && !waiveOffResult?.isApproved?.approved);
	}

	ngOnDestroy() {
		if (this.sseSubscription) {
			this.sseSubscription.unsubscribe();
		}
	}

	// Debts / Loan Calculator steps, shown one at a time (replaces the old tabs)
	activeStep: 'debts' | 'calculator' = 'debts';

	goToDebts(): void {
		this.activeStep = 'debts';
	}

	@ViewChild(LoancalculatorComponent) private sahaCalculator?: LoancalculatorComponent;
	@ViewChild(OldLoanCalculatorComponent) private oldCalculator?: OldLoanCalculatorComponent;

	// Land on a fresh calculator so the user has to Submit again with the updated debts
	goToCalculator(): void {
		this.activeStep = 'calculator';
		this.sahaCalculator?.resetForm();
		this.oldCalculator?.resetForm();
	}

	// Nothing to update: go straight to the calculator and keep what was saved
	skipToCalculator(): void {
		this.activeStep = 'calculator';
	}

}
